// Central glossary for the Investment 101 tab.
// Terms are looked up case-insensitively by `term` (not by slug) so authors can
// wrap plain-English phrases in <G>yield-on-cost</G> without maintaining ids.
// Keep definitions ≤ 2 sentences — hover popovers should not scroll.

export const GLOSSARY = [
  {
    term: "Dividend",
    letter: "D",
    definition:
      "A cash payment a company sends its shareholders — usually every quarter — out of its profits. Not every company pays one; those that do are called dividend stocks.",
  },
  {
    term: "Dividend yield",
    letter: "D",
    definition:
      "Annual dividend ÷ current share price, expressed as a percent. A stock at $100 paying $3/year has a 3% yield. The number changes every day because the price moves.",
  },
  {
    term: "Yield-on-cost",
    aliases: ["yield on cost", "YoC"],
    letter: "Y",
    definition:
      "Annual dividend ÷ your original purchase price. Unlike current yield, YoC only goes up when the dividend grows — it's the number that shows the compounding you've earned over time.",
  },
  {
    term: "DRIP",
    aliases: ["dividend reinvestment", "reinvestment"],
    letter: "D",
    definition:
      "Dividend Reinvestment Plan — every dividend you receive is automatically used to buy more shares of the same stock, commission-free. It's how a $10k position becomes $47k over 20 years.",
  },
  {
    term: "CAGR",
    aliases: ["compound annual growth rate"],
    letter: "C",
    definition:
      "Compound Annual Growth Rate. The average annualized return of an investment, assuming profits are reinvested. If $10k becomes $20k in 7.2 years, CAGR is ~10%.",
  },
  {
    term: "Total return",
    letter: "T",
    definition:
      "Everything you got back: capital appreciation (price going up) plus dividends received. Growth stocks vs dividend stocks is a false choice — total return is what matters.",
  },
  {
    term: "Ex-dividend date",
    aliases: ["ex-div", "ex-dividend"],
    letter: "E",
    definition:
      "The cutoff day for owning a stock to receive its next dividend. If you buy on or after the ex-div date, you miss that quarter's payment — the seller gets it.",
  },
  {
    term: "Payout ratio",
    letter: "P",
    definition:
      "Percent of earnings a company pays out as dividends. Under 60% is comfortable; over 100% means they're paying out more than they earn — usually a red flag.",
  },
  {
    term: "Dividend growth rate",
    aliases: ["DGR"],
    letter: "D",
    definition:
      "How fast a company raises its dividend, expressed as an annual %. A 3% yield growing 10%/year doubles to 6% yield-on-cost in ~7 years — that's the snowball.",
  },
  {
    term: "Backtest",
    letter: "B",
    definition:
      "Running a portfolio's rules over historical data to see how it would have performed. Not a promise — but the closest thing you can get to a time machine before risking real money.",
  },
  {
    term: "Drawdown",
    letter: "D",
    definition:
      "The percent your portfolio drops from its peak. A -30% drawdown means you're down 30% from the highest point — an important measure of how much pain you'd have to sit through.",
  },
  {
    term: "Sharpe ratio",
    letter: "S",
    definition:
      "Return per unit of risk. Higher = better. A Sharpe of 1.0 is respectable; 2.0 is excellent. If two portfolios have the same CAGR, the one with a higher Sharpe is safer.",
  },
  {
    term: "Sequence risk",
    letter: "S",
    definition:
      "The bad luck of hitting a market crash right after you retire. Dividend income partially defuses it — you don't have to sell shares at bad prices to fund living expenses.",
  },
  {
    term: "Dividend safety",
    letter: "D",
    definition:
      "A measure of how likely a company is to keep paying its dividend. Combines payout ratio, cash flow, debt, and dividend history. DPP's Ticker Research tab surfaces this.",
  },
  {
    term: "Dividend aristocrat",
    letter: "D",
    definition:
      "An S&P 500 company that has raised its dividend for 25+ consecutive years. Only ~65 companies qualify. Examples: Procter & Gamble, Coca-Cola, Johnson & Johnson.",
  },
  {
    term: "Dividend king",
    letter: "D",
    definition:
      "A company that has raised its dividend for 50+ consecutive years. Even more elite than aristocrats — fewer than 55 exist globally.",
  },
  {
    term: "REIT",
    aliases: ["real estate investment trust"],
    letter: "R",
    definition:
      "Real Estate Investment Trust — a company that owns income-producing real estate. Required by law to pay out 90% of taxable income as dividends, so they often yield 4–8%.",
  },
  {
    term: "Covered call ETF",
    letter: "C",
    definition:
      "An ETF that sells options against its stock holdings to generate extra income (e.g. JEPI, QYLD). Higher current yield but usually lower total return vs a plain index fund.",
  },
  {
    term: "SWR",
    aliases: ["safe withdrawal rate"],
    letter: "S",
    definition:
      "Safe Withdrawal Rate — the % of your portfolio you can pull each year in retirement without running out. The classic answer is 4%, but dividend income can push this to 5–6% safely.",
  },
  {
    term: "Yield trap",
    letter: "Y",
    definition:
      "A high-yield stock (typically 8%+) that looks amazing but is really signaling danger — the market is pricing in a dividend cut. If it sounds too good to be true, it usually is.",
  },
  {
    term: "Rebalancing",
    letter: "R",
    definition:
      "Periodically buying/selling to bring your portfolio back to its target weights. A stock that grew to 40% of your portfolio when you wanted 20% is now concentrated risk.",
  },
  {
    term: "Expense ratio",
    letter: "E",
    definition:
      "Annual fee an ETF or fund charges, expressed as a % of assets. SCHD is 0.06%, JEPI is 0.35%. Compounded over 30 years, even 0.5% can eat six figures.",
  },
  {
    term: "Snowball effect",
    letter: "S",
    definition:
      "The compounding of reinvested dividends buying shares that pay more dividends that buy more shares. Small at first, unstoppable by year 20.",
  },
  {
    term: "Qualified dividend",
    letter: "Q",
    definition:
      "A dividend taxed at the lower capital-gains rate (0%, 15%, or 20%) instead of ordinary income. Most US large-cap dividends qualify if you hold the stock 60+ days.",
  },
  {
    term: "Dollar-cost averaging",
    aliases: ["DCA"],
    letter: "D",
    definition:
      "Investing a fixed amount at regular intervals regardless of price. Removes the temptation to time the market and smooths out entry price over time.",
  },
];

// Build a case-insensitive lookup so <G>YoC</G> and <G>yoc</G> resolve equally.
const _lookup = (() => {
  const map = new Map();
  for (const g of GLOSSARY) {
    map.set(g.term.toLowerCase(), g);
    for (const a of g.aliases || []) map.set(a.toLowerCase(), g);
  }
  return map;
})();

export function findTerm(name) {
  return _lookup.get(String(name || "").toLowerCase().trim());
}
